import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { moneyFmt, sanitizeFilename, imageToPngDataURL } from "./helpers";
import logoDefault from "../assets/almaquinta.svg";

// Color corporativo para titulos y headers de tabla
const BRAND_COLOR = [69, 115, 115]; // #457373

// Convertir HTML a texto plano con formato para jspdf-autotable
function htmlToPlainText(html) {
    if (!html) return '';

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    let result = '';

    function processNode(node, insideListItem = false) {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            if (text && text.trim()) {
                result += text;
            }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();

            // Manejar listas
            if (tagName === 'ul' || tagName === 'ol') {
                if (result.length > 0 && !result.endsWith('\n')) {
                    result += '\n';
                }
                node.childNodes.forEach(child => processNode(child, false));
                return;
            }

            // Elemento de lista
            if (tagName === 'li') {
                if (result.length > 0 && !result.endsWith('\n')) {
                    result += '\n';
                }
                result += '• ';
                // Procesar hijos indicando que estamos dentro de un li
                node.childNodes.forEach(child => processNode(child, true));
                if (!result.endsWith('\n')) {
                    result += '\n';
                }
                return;
            }

            // Salto de linea
            if (tagName === 'br') {
                result += '\n';
                return;
            }

            // Parrafos y divs - NO agregar salto si estamos dentro de un li (TipTap envuelve en <p>)
            if (tagName === 'p' || tagName === 'div') {
                if (!insideListItem && result.length > 0) {
                    // Doble salto entre párrafos para separación visual
                    if (!result.endsWith('\n\n')) {
                        result += result.endsWith('\n') ? '\n' : '\n\n';
                    }
                }
                node.childNodes.forEach(child => processNode(child, insideListItem));
                if (!insideListItem && result.length > 0 && !result.endsWith('\n')) {
                    result += '\n';
                }
                return;
            }

            // Otros elementos (b, i, strong, em, span, etc.) - solo procesar hijos
            node.childNodes.forEach(child => processNode(child, insideListItem));
        }
    }

    tempDiv.childNodes.forEach(child => processNode(child, false));

    // Limpiar: remover lineas que solo tienen viñeta sin contenido, trim cada linea
    return result
        .split('\n')
        .map(line => line.trim())
        .filter(line => line !== '•' && line !== '-') // Remover lineas con solo viñeta
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

// Renderizar HTML en PDF con soporte para multiples paginas
// Parsear HTML a lineas con segmentos de formato [{segments: [{text, bold, italic}]}]
// Una linea con segments vacío representa un salto de linea
function htmlToFormattedLines(html) {
    if (!html) return [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const lines = [];
    let currentLine = [];

    function pushLine(forceEmpty = false) {
        if (currentLine.length > 0) {
            lines.push({ segments: currentLine });
            currentLine = [];
        } else if (forceEmpty) {
            lines.push({ segments: [] });
        }
    }

    function processNode(node, bold = false, italic = false, insideListItem = false) {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            if (text && text.trim()) {
                currentLine.push({ text: text.trim(), bold, italic });
            }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = node.tagName.toLowerCase();
            const newBold = bold || tag === 'b' || tag === 'strong';
            const newItalic = italic || tag === 'i' || tag === 'em';

            if (tag === 'br') {
                pushLine();
            } else if (tag === 'ul' || tag === 'ol') {
                pushLine();
                node.childNodes.forEach(child => processNode(child, newBold, newItalic, false));
            } else if (tag === 'li') {
                pushLine();
                currentLine.push({ text: '• ', bold: newBold, italic: newItalic });
                node.childNodes.forEach(child => processNode(child, newBold, newItalic, true));
                pushLine();
            } else if (tag === 'p' || tag === 'div') {
                if (!insideListItem) {
                    // Si el parrafo esta vacio, agregar linea vacia (salto)
                    const hasContent = node.textContent && node.textContent.trim();
                    pushLine();
                    if (!hasContent) {
                        pushLine(true);
                        return;
                    }
                }
                node.childNodes.forEach(child => processNode(child, newBold, newItalic, insideListItem));
                if (!insideListItem) pushLine();
            } else {
                node.childNodes.forEach(child => processNode(child, newBold, newItalic, insideListItem));
            }
        }
    }

    tempDiv.childNodes.forEach(child => processNode(child));
    pushLine();

    // Remover lineas vacias al inicio y al final, pero preservar las del medio
    while (lines.length > 0 && lines[0].segments.length === 0) lines.shift();
    while (lines.length > 0 && lines[lines.length - 1].segments.length === 0) lines.pop();

    return lines;
}

function renderHtmlToPdfWithPages(doc, html, startX, startY, maxWidth, pageHeight, bottomMargin) {
    if (!html) return startY;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    let currentY = startY;
    const lineHeight = 11;
    const listIndent = 10;
    let pendingNewLine = false; // Flag para saber si hay un salto pendiente

    const checkNewPage = () => {
        if (currentY + lineHeight > pageHeight - bottomMargin) {
            doc.addPage();
            currentY = 50;
        }
    };

    // Extraer texto plano de un nodo (para items de lista)
    function getTextContent(node) {
        let text = '';
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent || '';
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            node.childNodes.forEach(child => {
                text += getTextContent(child);
            });
        }
        return text;
    }

    function processNode(node, isBold = false, isItalic = false, indent = 0, align = 'left') {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent?.trim();
            if (text) {
                // Si hay un salto pendiente, aplicarlo ahora antes de dibujar texto
                if (pendingNewLine) {
                    pendingNewLine = false;
                }

                let fontStyle = 'normal';
                if (isBold && isItalic) fontStyle = 'bolditalic';
                else if (isBold) fontStyle = 'bold';
                else if (isItalic) fontStyle = 'italic';

                doc.setFont('helvetica', fontStyle);
                doc.setFontSize(9);

                const availableWidth = maxWidth - indent;
                const lines = doc.splitTextToSize(text, availableWidth);
                lines.forEach(line => {
                    checkNewPage();
                    // Aplicar alineación
                    let xPos = startX + indent;
                    let textAlign = undefined;
                    if (align === 'right') {
                        xPos = startX + maxWidth;
                        textAlign = { align: 'right' };
                    } else if (align === 'center') {
                        xPos = startX + (maxWidth / 2);
                        textAlign = { align: 'center' };
                    }
                    doc.text(line, xPos, currentY, textAlign);
                    currentY += lineHeight;
                });
            }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tagName = node.tagName.toLowerCase();
            const newBold = isBold || tagName === 'b' || tagName === 'strong';
            const newItalic = isItalic || tagName === 'i' || tagName === 'em';

            // Detectar alineación del elemento
            const styleAttr = node.getAttribute('style') || '';
            let newAlign = align;
            if (styleAttr.includes('text-align: right') || styleAttr.includes('text-align:right')) {
                newAlign = 'right';
            } else if (styleAttr.includes('text-align: center') || styleAttr.includes('text-align:center')) {
                newAlign = 'center';
            } else if (styleAttr.includes('text-align: left') || styleAttr.includes('text-align:left')) {
                newAlign = 'left';
            }

            if (tagName === 'br') {
                // El <br> no debe agregar linea extra porque el texto anterior ya avanzo
                // Solo marca que hay un salto pendiente (para casos de <br><br>)
                pendingNewLine = true;
            } else if (tagName === 'hr') {
                // Linea horizontal
                checkNewPage();
                currentY += lineHeight * 0.3;
                doc.setDrawColor(150, 150, 150);
                doc.setLineWidth(0.5);
                doc.line(startX, currentY, startX + maxWidth, currentY);
                currentY += lineHeight * 0.8;
                pendingNewLine = false;
            } else if (tagName === 'ul' || tagName === 'ol') {
                // Procesar cada hijo de la lista
                node.childNodes.forEach(child => processNode(child, newBold, newItalic, indent, newAlign));
            } else if (tagName === 'li') {
                checkNewPage();

                // Dibujar viñeta
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(9);
                doc.text('•', startX + indent, currentY);

                // Procesar hijos del li respetando negritas/cursivas
                node.childNodes.forEach(child => processNode(child, newBold, newItalic, indent + listIndent, newAlign));
                pendingNewLine = false;
            } else if (tagName === 'div' || tagName === 'p') {
                // Para p y div, procesar contenido normalmente
                const hasContent = node.textContent?.trim();
                node.childNodes.forEach(child => processNode(child, newBold, newItalic, indent, newAlign));
                // Agregar espacio despues de parrafos (separación entre bloques)
                if (node.parentElement?.tagName?.toLowerCase() !== 'li') {
                    if (!hasContent) {
                        // Parrafo vacio = salto de linea extra
                        currentY += lineHeight * 0.5;
                    } else {
                        // Parrafo con contenido = espacio de separación entre párrafos
                        currentY += lineHeight * 0.4;
                    }
                }
                pendingNewLine = false;
            } else {
                node.childNodes.forEach(child => processNode(child, newBold, newItalic, indent, newAlign));
            }
        }
    }

    tempDiv.childNodes.forEach(child => processNode(child, false, false, 0, 'left'));

    return currentY;
}

export async function generatePdfCotizacion({
    cotizacion,
    servicios,
    extras,
    configuracion,
    tiposServicio,
    preview = false
}) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;
    const bottomMargin = 40;

    const currency = cotizacion.moneda || 'PEN';
    const igvRate = parseFloat(configuracion.igv_rate) || 0.18;

    // Resolver URL del logo (usa el de configuracion o el default)
    const logoConfig = configuracion?.empresa_logo;
    let logoUrl;
    if (logoConfig) {
        if (logoConfig.startsWith('data:') || logoConfig.startsWith('http')) {
            logoUrl = logoConfig;
        } else {
            const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
            logoUrl = `${baseUrl}${logoConfig}`;
        }
    } else {
        logoUrl = logoDefault;
    }

    // Cargar logo y convertir a PNG (jsPDF no soporta SVG directamente)
    const logoDataUrl = await imageToPngDataURL(logoUrl, 200);

    // Header layout
    const headerTop = 44;
    const logoSize = 50;

    if (logoDataUrl) {
        try {
            doc.addImage(
                logoDataUrl,
                "PNG",
                marginX,
                headerTop - 26,
                logoSize,
                logoSize
            );
        } catch {}
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...BRAND_COLOR);
    doc.text(
        `COTIZACIÓN DE SERVICIOS - ${cotizacion.numero}`,
        logoDataUrl ? marginX + logoSize + 10 : marginX,
        headerTop
    );
    doc.setTextColor(0, 0, 0); // Restaurar color negro

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    const companyLines = [
        configuracion.empresa_nombre || "Alma Industria Creativa E.I.R.L.",
        configuracion.empresa_ruc ? `RUC: ${configuracion.empresa_ruc}` : "",
        [configuracion.empresa_email, configuracion.empresa_telefono]
            .filter(Boolean)
            .join(" | "),
    ].filter(Boolean);

    // Formatear fecha legible (DD/MM/YYYY) - sin problemas de timezone
    const formatearFecha = (fecha) => {
        if (!fecha) return '';
        // Si viene como string "YYYY-MM-DD", parsear directamente para evitar problemas de timezone
        if (typeof fecha === 'string' && fecha.match(/^\d{4}-\d{2}-\d{2}/)) {
            const [anio, mes, dia] = fecha.split('T')[0].split('-');
            return `${dia}/${mes}/${anio}`;
        }
        // Fallback para otros formatos
        const d = new Date(fecha);
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const anio = d.getFullYear();
        return `${dia}/${mes}/${anio}`;
    };

    const rightLines = [
        { text: `Fecha: ${formatearFecha(cotizacion.fecha)}`, bold: false },
        { text: `Cliente: ${cotizacion.cliente_nombre}`, bold: false },
        cotizacion.cliente_empresa ? { text: `Empresa: ${cotizacion.cliente_empresa}`, bold: false } : null,
        cotizacion.tiempo_desarrollo ? { text: `Tiempo: ${cotizacion.tiempo_desarrollo}`, bold: false } : null,
        { text: `Moneda: ${currency}`, bold: false },
        { text: `Validez: ${cotizacion.validez} días`, bold: false },
    ].filter(Boolean);

    // Asegurar que infoY este debajo del logo (logo termina en headerTop - 26 + logoSize)
    const logoBottom = logoDataUrl ? (headerTop - 26 + logoSize) : headerTop;
    const infoY = Math.max(headerTop + 18, logoBottom + 12);

    companyLines.forEach((line, i) => {
        doc.text(line, marginX, infoY + i * 14);
    });

    const rightX = pageWidth - marginX;
    rightLines.forEach((item, i) => {
        doc.setFont("helvetica", item.bold ? "bold" : "normal");
        doc.text(item.text, rightX, infoY + i * 14, { align: "right" });
    });
    doc.setFont("helvetica", "normal"); // Restaurar

    // Agrupar servicios por categoria
    const serviciosPorCategoria = {};

    servicios.forEach((item) => {
        const tipoServicio = tiposServicio.find(t => t.id === item.tipo_servicio_id);
        const categoriaNombre = tipoServicio?.categoria_nombre || 'Otros Servicios';

        if (!serviciosPorCategoria[categoriaNombre]) {
            serviciosPorCategoria[categoriaNombre] = [];
        }

        serviciosPorCategoria[categoriaNombre].push({
            nombre: item.tipo_servicio_nombre || tipoServicio?.nombre || 'Servicio',
            detalle: item.detalle || '',
            total: parseFloat(item.total) || 0
        });
    });

    let currentTableY = infoY + 100;

    // Generar una tabla por cada categoria
    Object.entries(serviciosPorCategoria).forEach(([categoria, items]) => {
        // Titulo de la categoria
        if (currentTableY + 60 > pageHeight - bottomMargin) {
            doc.addPage();
            currentTableY = 50;
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...BRAND_COLOR);
        doc.text(categoria, marginX, currentTableY);
        doc.setTextColor(0, 0, 0);
        currentTableY += 16;

        // Header de tabla
        const tableLeft = marginX;
        const tableWidth = 510;
        const col0Width = 420;
        const col1Width = 90;
        const cellPad = 8;

        // Dibujar header
        doc.setFillColor(...BRAND_COLOR);
        doc.rect(tableLeft, currentTableY, tableWidth, 22, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.text('Servicio', tableLeft + cellPad, currentTableY + 15);
        doc.text('Importe', tableLeft + col0Width + col1Width - cellPad, currentTableY + 15, { align: 'right' });
        doc.setTextColor(0, 0, 0);
        currentTableY += 22;

        // Dibujar cada servicio
        items.forEach((item) => {
            const detailMaxWidth = col0Width - (cellPad * 2);

            // Verificar si cabe al menos el nombre en esta pagina
            if (currentTableY + 30 > pageHeight - bottomMargin) {
                doc.addPage();
                currentTableY = 50;
            }

            // Guardar pagina y posicion inicial
            const startPage = doc.internal.getNumberOfPages();
            const rowStartY = currentTableY;
            let contentY = currentTableY + cellPad + 9;

            // Nombre del servicio (siempre bold)
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            const titleLines = doc.splitTextToSize(item.nombre, detailMaxWidth);
            titleLines.forEach(line => {
                doc.text(line, tableLeft + cellPad, contentY);
                contentY += 11;
            });

            // Importe (junto al nombre, arriba a la derecha)
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text(moneyFmt(item.total, currency), tableLeft + col0Width + col1Width - cellPad, rowStartY + cellPad + 9, { align: 'right' });

            // Detalle con formato usando renderHtmlToPdfWithPages
            if (item.detalle) {
                contentY = renderHtmlToPdfWithPages(doc, item.detalle, tableLeft + cellPad, contentY, detailMaxWidth, pageHeight, bottomMargin);
            }

            const endPage = doc.internal.getNumberOfPages();
            const rowEndY = contentY + cellPad;

            // Dibujar bordes
            doc.setDrawColor(200, 200, 200);

            if (startPage === endPage) {
                // Todo en una pagina: bordes simples
                const rowHeight = rowEndY - rowStartY;
                doc.rect(tableLeft, rowStartY, col0Width, rowHeight);
                doc.rect(tableLeft + col0Width, rowStartY, col1Width, rowHeight);
            } else {
                // Multi-pagina: bordes en cada pagina
                // Pagina inicial: desde rowStartY hasta el fondo
                const currentPageNum = endPage;
                for (let p = startPage; p <= endPage; p++) {
                    doc.setPage(p);
                    doc.setDrawColor(200, 200, 200);
                    const topY = p === startPage ? rowStartY : 42;
                    const botY = p === endPage ? rowEndY : pageHeight - bottomMargin;
                    const h = botY - topY;
                    doc.rect(tableLeft, topY, col0Width, h);
                    doc.rect(tableLeft + col0Width, topY, col1Width, h);
                }
                // Volver a la ultima pagina
                doc.setPage(currentPageNum);
            }

            currentTableY = rowEndY;
        });

        currentTableY += 20;
    });

    // Tabla de extras (si hay)
    if (extras.length > 0) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...BRAND_COLOR);
        doc.text("Extras", marginX, currentTableY);
        doc.setTextColor(0, 0, 0);
        currentTableY += 16;

        const extrasBody = extras.map(extra => [
            htmlToPlainText(extra.detalle) || 'Extra',
            moneyFmt(parseFloat(extra.costo) || 0, currency)
        ]);

        autoTable(doc, {
            startY: currentTableY,
            head: [["Descripcion", "Importe"]],
            body: extrasBody,
            theme: "grid",
            styles: {
                font: "helvetica",
                fontSize: 9,
                cellPadding: 8,
                valign: "top",
            },
            headStyles: { fontStyle: "bold", fillColor: BRAND_COLOR },
            columnStyles: {
                0: { cellWidth: 420 },
                1: { cellWidth: 90, halign: "right" },
            },
        });

        currentTableY = doc.lastAutoTable?.finalY || currentTableY + 30;
    }

    // Funcion auxiliar para verificar si necesitamos nueva pagina
    const checkNewPage = (currentY, requiredSpace = 100) => {
        if (currentY + requiredSpace > pageHeight - bottomMargin) {
            doc.addPage();
            return 50; // Margen superior en nueva pagina
        }
        return currentY;
    };

    // Verificar espacio para totales (aproximadamente 100pt)
    // Agregar mas espacio entre la tabla y los totales
    let currentY = checkNewPage(currentTableY + 25, 100);

    // Alinear con el borde derecho de la tabla (marginX + 420 + 90 = marginX + 510)
    const tableRightEdge = marginX + 510;
    const labelX = tableRightEdge - 200;
    const valueX = tableRightEdge;

    const subtotal = parseFloat(cotizacion.subtotal) || 0;
    const subtotalExtras = parseFloat(cotizacion.subtotalExtras) || 0;
    const igv = parseFloat(cotizacion.igv) || 0;
    const total = parseFloat(cotizacion.total) || 0;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    doc.text("Subtotal", labelX, currentY);
    doc.text(moneyFmt(subtotal, currency), valueX, currentY, { align: "right" });
    currentY += 16;

    doc.text(`IGV (${Math.round(igvRate * 100)}%)`, labelX, currentY);
    doc.text(moneyFmt(igv, currency), valueX, currentY, { align: "right" });
    currentY += 16;

    // Mostrar subtotal extras solo si hay extras
    if (subtotalExtras > 0) {
        doc.text("Subtotal Extras", labelX, currentY);
        doc.text(moneyFmt(subtotalExtras, currency), valueX, currentY, { align: "right" });
        currentY += 20;
    } else {
        currentY += 4;
    }

    doc.setFont("helvetica", "bold");
    doc.text("TOTAL", labelX, currentY);
    doc.text(moneyFmt(total, currency), valueX, currentY, { align: "right" });
    currentY += 30;

    // Comentarios
    if (cotizacion.comentarios) {
        const comentariosTexto = htmlToPlainText(cotizacion.comentarios);
        if (comentariosTexto) {
            currentY = checkNewPage(currentY, 150);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(9);
            doc.text("Comentarios:", marginX, currentY);
            doc.setFont("helvetica", "normal");
            currentY += 14;

            // Renderizar HTML con formato (negritas, cursivas, listas, alineación)
            currentY = renderHtmlToPdfWithPages(doc, cotizacion.comentarios, marginX, currentY, pageWidth - marginX * 2, pageHeight, bottomMargin);
        }
    }

    // Metodos de pago
    if (configuracion.metodos_pago) {
        // Estimar altura de metodos de pago
        currentY = checkNewPage(currentY, 150);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("Metodos de Pago:", marginX, currentY);
        doc.setFont("helvetica", "normal");
        currentY += 14;

        // Renderizar HTML con manejo de paginas
        currentY = renderHtmlToPdfWithPages(doc, configuracion.metodos_pago, marginX, currentY, pageWidth - marginX * 2, pageHeight, bottomMargin);
    }

    const fileName = sanitizeFilename(
        `${cotizacion.numero}_${cotizacion.cliente_nombre}_cotizacion`
    );

    if (preview) {
        // Abrir en nueva pestaña para previsualizar
        const pdfBlob = doc.output('blob');
        const blobUrl = URL.createObjectURL(pdfBlob);
        window.open(blobUrl, '_blank');
    } else {
        // Descargar directamente
        doc.save(`${fileName}.pdf`);
    }
}

export default generatePdfCotizacion;
